import type { FloatingPosition } from '@mantine/core';
import { Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '~/common/string/classname';
import { PRISMA_MAX_STRING_LENGTH } from '~/common/db/constants';

export default function EditableTitle({
  title,
  tooltip,
  tooltipPosition,
  handleSave,
  readOnly,
  classNames,
  maxWidth,
  onClick,
}: {
  title: string;
  tooltip?: string;
  readOnly?: boolean;
  handleSave: (newTitle: string) => Promise<void>;
  tooltipPosition?: FloatingPosition;
  classNames?: {
    input?: string;
    text?: string;
  };
  maxWidth?: number;
  onClick?: () => void;
}) {
  const [titleStr, setTitleStr] = useState(title);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const showEditor = isEditing && !readOnly;

  const measureRef = useAutoResizeInput(inputRef, titleStr, maxWidth);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setTitleStr(e.target.value);
  };

  const saveTitle = async (newTitle: string) => {
    if (isSaving) {
      return;
    }
    setIsEditing(false);

    if (newTitle.length > PRISMA_MAX_STRING_LENGTH) {
      notifications.show({
        message: `Title is too long. Please use ${PRISMA_MAX_STRING_LENGTH} characters or fewer.`,
        color: 'red',
      });
      setTitleStr(title);
      return;
    }
    if (newTitle === title) {
      return;
    }

    setIsSaving(true);
    try {
      const finalTitle = newTitle || 'Untitled Test';
      setTitleStr(finalTitle);
      await handleSave(finalTitle);
    } catch (error) {
      notifications.show({
        message: 'Error saving title.',
        color: 'red',
      });
      setTitleStr(title);
    } finally {
      setIsSaving(false);
    }
  };

  // TODO: Extract shared logic with EditableMultilineTitle into useEditableTitle hook
  // https://app.dartai.com/t/xzSu2e7G1qBd-Refactor-Extract-useEditableTi
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      await saveTitle(titleStr);
    }
  };

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  // In case we rename the title with AI, we need to update the input value
  useEffect(() => {
    setTitleStr(title);
  }, [title]);

  return (
    <div className="relative flex min-w-0 max-w-full items-center">
      <input
        ref={inputRef}
        className={cn(
          'min-w-[100px] max-w-full rounded p-2 outline-none',
          {
            'invisible absolute': !showEditor,
          },
          classNames?.input,
        )}
        value={titleStr}
        readOnly={readOnly}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={async () => await saveTitle(titleStr)}
      />
      <Tooltip
        label={readOnly ? 'Please wait...' : tooltip}
        position={tooltipPosition || 'top-start'}
        disabled={!tooltip}
      >
        <Text
          className={cn(
            'text-md max-w-full truncate font-bold',
            {
              hidden: showEditor,
              'cursor-not-allowed opacity-70': readOnly,
              'cursor-pointer': !readOnly,
            },
            classNames?.text,
          )}
          onClick={() => {
            if (onClick) {
              onClick();
            }
            if (!readOnly) {
              setIsEditing(true);
            }
          }}
        >
          <span className="truncate">{titleStr}</span>
        </Text>
      </Tooltip>
      <span
        ref={measureRef}
        className={cn('invisible absolute -z-10 whitespace-pre px-2', classNames?.input)}
      />
    </div>
  );
}

function useAutoResizeInput(inputRef: React.RefObject<HTMLInputElement | null>, text: string, maxWidth?: number) {
  const measureRef = useRef<HTMLSpanElement>(null);

  const updateInputWidth = useCallback(() => {
    if (measureRef.current && inputRef.current) {
      const textWidth = measureRef.current.offsetWidth;
      const newWidth = maxWidth && textWidth > maxWidth ? maxWidth : textWidth;
      inputRef.current.style.width = `${newWidth + 2}px`;
    }
  }, [inputRef, maxWidth]);

  useEffect(() => {
    if (measureRef.current) {
      measureRef.current.textContent = text || '';
    }
    updateInputWidth();
  }, [text, updateInputWidth]);

  useEffect(() => {
    updateInputWidth();
  }, [inputRef, updateInputWidth]);

  return measureRef;
}
